import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useI18N } from "@sundae/react-hooks";
import { ErrorDialog, Notice, Text } from "@sundae/ui-toolkit";
import { useWalletDetails } from "@sundae/wallet-react";
import { useMutation } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { deviceDetect } from "react-device-detect";
import { serializeError } from "serialize-error";
import ErrorDetailsMessage from "../../i18n/components/error/ErrorDetailsMessage/index.js";
import { postData } from "../../utils/postData.js";
import CopyText from "../CopyText/index.js";
const ErrorBox = ({ reportID, type, message, originalError, details, reset, }) => {
    const { t } = useI18N("error-fallback");
    const [sent, setSent] = useState(false);
    const [open, setOpen] = useState(false);
    const serializedOriginalError = useMemo(() => JSON.stringify(serializeError(originalError), (_key, value) => typeof value === "bigint" ? value.toString() : value), [originalError]);
    const { mutate, isLoading } = useMutation({
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        mutationFn: (reportData) => postData(window.__APP_CONFIG.apiUrls.errors, reportData),
        onSettled: () => setSent(true),
        onError: (error) => {
            console.error(error);
        },
    });
    const { activeWallet, details: { mainAddress, network }, } = useWalletDetails();
    const walletInfo = window.cardano?.[activeWallet];
    const formattedDate = new Date().toISOString();
    const deviceInfo = useMemo(() => deviceDetect(window.navigator.userAgent), []);
    const browser = `${deviceInfo.browserName} ${deviceInfo.browserFullVersion}`;
    const os = `${deviceInfo.osName} ${deviceInfo.osVersion}`;
    const reportMessage = useMemo(() => `
      ${sent ? `Report ID: ${reportID}` : ``}
      ${walletInfo ? `Wallet: ${walletInfo.name} ${walletInfo.apiVersion}` : ""}
      ${`Wallet address: ${mainAddress}`}
      Message: ${message}
      Original Error: ${serializedOriginalError}
      Details: ${JSON.stringify(details)}
      Date: ${formattedDate}
      Browser: ${browser}
      OS: ${os}
    `, [
        reportID,
        mainAddress,
        formattedDate,
        browser,
        os,
        message,
        serializedOriginalError,
        details,
    ]);
    const onSendReport = () => {
        mutate({
            reportID,
            walletAddress: mainAddress,
            ...(walletInfo && {
                walletInfo: `${walletInfo.name} ${walletInfo.apiVersion}`,
            }),
            browser,
            localTimestamp: formattedDate,
            message: message,
            originalError: serializedOriginalError,
            details,
            os,
            url: window.location.href,
        });
    };
    if (type === "info" || type === "warning") {
        return _jsx(Notice, { type: type, children: message });
    }
    return (_jsx(_Fragment, { children: _jsxs(ErrorDialog, { cancelButtonText: sent ? "Reset" : t("dontSend"), className: "mx-auto w-full max-w-xl", confirmButtonText: sent ? "Sent!" : t("sentReport"), errorReport: _jsxs("div", { className: "grid gap-2", children: [_jsxs("div", { children: [_jsxs(Text, { tag: "div", variant: "warning", children: [t("reportID", { ns: "common" }), ":"] }), reportID] }), walletInfo && (_jsxs("div", { children: [_jsxs(Text, { tag: "div", variant: "warning", children: [t("wallet", { ns: "glossary" }), ":"] }), walletInfo.name, " ", walletInfo.apiVersion] })), mainAddress && (_jsxs("div", { children: [_jsxs(Text, { tag: "div", variant: "warning", children: [t("walletAddress"), ":"] }), mainAddress] })), _jsxs("div", { children: [_jsxs(Text, { tag: "div", variant: "warning", children: [t("errorMessage"), ":"] }), originalError?.message] }), _jsxs("div", { children: [_jsxs(Text, { tag: "div", variant: "warning", children: [t("date", { ns: "common" }), ":"] }), formattedDate] }), _jsxs("div", { children: [_jsxs(Text, { tag: "div", variant: "warning", children: [t("browser", { ns: "common" }), ":"] }), browser] }), _jsxs("div", { children: [_jsxs(Text, { tag: "div", variant: "warning", children: [t("os", { ns: "common" }), ":"] }), os] }), _jsxs("div", { children: [_jsxs(Text, { tag: "div", variant: "warning", children: [t("details", { ns: "common" }), ":"] }), _jsx("pre", { children: JSON.stringify(details) })] })] }), isButtonDisabled: isLoading || sent, isCancelButtonDisabled: isLoading, onCancel: () => {
                reset?.();
            }, onConfirm: onSendReport, open: open, reportMessage: reportMessage, title: t("title"), trigger: _jsx(ErrorDetailsMessage, { toggleCollapsible: () => setOpen(!open) }), children: [sent && (_jsxs(Text, { tag: "div", className: "flex flex-row justify-around align-baseline", children: [t("reportID", { ns: "common" }), ":", " ", _jsx(CopyText, { tooltipText: reportID, visibleText: reportID })] })), _jsx(Notice, { type: "error", children: message })] }) }));
};
export default ErrorBox;
//# sourceMappingURL=ErrorBox.js.map